import { isEmpty, isNotEmpty } from '@/shared/util';

import { createQuillListItem } from './builder.util';
import {
  ORDERED_LIST_MARKER_PATTERN,
  WORD_LIST_BLOCK_SELECTOR,
  WORD_LIST_MARKER_IGNORE_PATTERN,
  WORD_MSO_LIST_STYLE_PATTERN,
} from './nested-list-clipboard.constant';
import { QuillListType, WordListBlock } from './nested-list-clipboard.model';
import { areNearbyListBlocks } from './nested-list-clipboard-dom.util';

export function hasWordFlatListParagraphs(html: string): boolean {
  return WORD_MSO_LIST_STYLE_PATTERN.test(html);
}

function getListTypeFromWordMarker(markerText: string): QuillListType {
  if (isEmpty(markerText)) {
    return 'bullet';
  }

  if (ORDERED_LIST_MARKER_PATTERN.test(markerText.trim())) {
    return 'ordered';
  }

  return 'bullet';
}

function getWordListMarkerText(element: HTMLElement): string {
  const markerSpan = element.querySelector('span[style*="mso-list"]');

  if (!(markerSpan instanceof HTMLElement)) {
    return '';
  }

  if (!WORD_LIST_MARKER_IGNORE_PATTERN.test(markerSpan.getAttribute('style') ?? '')) {
    return '';
  }

  return markerSpan.textContent ?? '';
}

function isWordListMarkerIgnoreSpan(element: Element): boolean {
  return (
    element.tagName === 'SPAN' &&
    WORD_LIST_MARKER_IGNORE_PATTERN.test(element.getAttribute('style') ?? '')
  );
}

function removeWordListMarkerIgnoreSpans(root: ParentNode): void {
  for (const span of Array.from(root.querySelectorAll('span'))) {
    if (isWordListMarkerIgnoreSpan(span)) {
      span.remove();
    }
  }
}

/** Clone paragraph content without Word's `mso-list:Ignore` glyph spans (they leak as text). */
function cloneWordListBlockContent(element: HTMLElement): DocumentFragment {
  const container = document.createElement('div');

  for (const child of Array.from(element.childNodes)) {
    container.appendChild(child.cloneNode(true));
  }

  removeWordListMarkerIgnoreSpans(container);

  const fragment = document.createDocumentFragment();

  while (container.firstChild) {
    fragment.appendChild(container.firstChild);
  }

  return fragment;
}

function hasWordListBlockContent(element: HTMLElement): boolean {
  const fragment = cloneWordListBlockContent(element);
  const container = document.createElement('div');

  container.appendChild(fragment);

  return isNotEmpty(container.textContent) || Boolean(container.querySelector('img, br'));
}

function parseWordListBlock(element: HTMLElement): null | WordListBlock {
  const style = element.getAttribute('style') ?? '';
  const match = style.match(WORD_MSO_LIST_STYLE_PATTERN);

  if (!match) {
    return null;
  }

  const wordLevel = Number.parseInt(match[2], 10);

  // Word `levelN` is 1-based; Quill uses ql-indent-0 omission / ql-indent-1+.
  return {
    element,
    indent: Math.max(wordLevel - 1, 0),
    listId: match[1],
    listType: getListTypeFromWordMarker(getWordListMarkerText(element)),
  };
}

function findWordListBlocks(root: ParentNode): WordListBlock[] {
  return Array.from(root.querySelectorAll(WORD_LIST_BLOCK_SELECTOR))
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .map(parseWordListBlock)
    .filter((block): block is WordListBlock => block !== null);
}

function groupConsecutiveWordListBlocks(blocks: WordListBlock[]): WordListBlock[][] {
  if (isEmpty(blocks)) {
    return [];
  }

  const groups: WordListBlock[][] = [[blocks[0]]];

  for (let index = 1; index < blocks.length; index += 1) {
    const previousBlock = blocks[index - 1];
    const currentBlock = blocks[index];
    const sameListId = previousBlock.listId === currentBlock.listId;
    const isNearby = areNearbyListBlocks(previousBlock.element, currentBlock.element);

    if (sameListId && isNearby) {
      groups[groups.length - 1].push(currentBlock);
      continue;
    }

    groups.push([currentBlock]);
  }

  return groups;
}

function convertWordListBlockGroup(group: WordListBlock[]): HTMLOListElement {
  const flattenedList = document.createElement('ol');

  for (const block of group) {
    if (!hasWordListBlockContent(block.element)) {
      continue;
    }

    flattenedList.appendChild(
      createQuillListItem({
        content: cloneWordListBlockContent(block.element),
        indent: block.indent,
        listType: block.listType,
      }),
    );
  }

  return flattenedList;
}

export function convertWordOfficeListParagraphs(root: ParentNode): boolean {
  const blocks = findWordListBlocks(root);

  if (isEmpty(blocks)) {
    return false;
  }

  let didConvert = false;

  for (const group of groupConsecutiveWordListBlocks(blocks)) {
    const flattenedList = convertWordListBlockGroup(group);
    const firstBlock = group[0]?.element;

    if (!firstBlock?.parentElement || isEmpty(Array.from(flattenedList.children))) {
      continue;
    }

    firstBlock.parentElement.insertBefore(flattenedList, firstBlock);

    for (const block of group) {
      block.element.remove();
    }

    didConvert = true;
  }

  return didConvert;
}
